import { useCollectionKey, useConfigMap, useGetViewConfig, useItemSelector, useUserPreference } from "hooks";
import type { FunctionComponent } from "react";
import React, { useContext, useEffect, useMemo } from "react";

import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";
import { getItemTypeForContent } from "screens/content/common/utils";
import type { SupportedItemKeys } from "state/selection/reducer";

import orderBy from "lodash/orderBy";
import { ContentViewExtractedAISection } from "screens/content/contentView/ContentViewExtractedAISection";
import { SectionContentLayout } from "screens/collection/components/utils";
import { ProjectDetailViewSkeleton } from "screens/collection/ProjectDetailViewSkeleton";
import { ContentFilterContext } from "screens/content";

interface Props {
  collectionId: string;
  isLoading: boolean;
}

export const ProjectDetailDefaultAIResultsLegacy: FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
  collectionId,
  isLoading,
}) => {
  const { setSelectedItems, resetSelectedItems } = useItemSelector();
  const { isHighlightsPanelOpen, isAddToCollectionModalOpen } = useAddToCharliContext();
  const configMap = useConfigMap();
  const collectionType = useCollectionKey(collectionId, "collectionType");
  const { availableContent } = useContext(ContentFilterContext);
  const injectDebugEntityPreference = useUserPreference("ui_inject_debug_entity");
  const aiResultResourceSections = useGetViewConfig("aiResultResourceSections", collectionType, configMap);
  useEffect(() => {
    injectDebugEntityPreference && console.log("Project Content Data", availableContent);
  }, [injectDebugEntityPreference, availableContent]);

  useEffect(() => {
    if (availableContent?.length) {
      const items =
        availableContent &&
        availableContent.reduce((cells, cell) => {
          const itemType = getItemTypeForContent(cell.type);
          cells[cell.mediaId] = { type: itemType };
          return cells;
        }, {} as { [id: string]: { type: SupportedItemKeys } });
      if (items) setSelectedItems(items);
    } else {
      resetSelectedItems();
    }
  }, [availableContent?.length, isHighlightsPanelOpen, isAddToCollectionModalOpen, availableContent, setSelectedItems, resetSelectedItems]);

  const aiResultResourceSectionContent = useMemo(() => {
    if (!aiResultResourceSections) {
      return [];
    }

    const sectionNamesToInclude = aiResultResourceSections.flatMap((section) => {
      if (typeof section === "string" && section.includes(",")) {
        return section.split(",").map((s) => s.trim());
      }
      return section;
    });

    return orderBy(
      availableContent?.filter((content) =>
        content.detailSections.some(
          (detailSection) => sectionNamesToInclude.includes(detailSection.sectionName) && detailSection.data.length > 0
        )
      ),
      "name"
    );
  }, [aiResultResourceSections, availableContent]);

  return (
    <ProjectDetailViewSkeleton isLoading={isLoading} height="100%">
      {aiResultResourceSectionContent &&
        aiResultResourceSectionContent.map((contentDetail, index) => (
          <SectionContentLayout useGrid className={`section-${index}`} key={`section-${index}`}>
            <ContentViewExtractedAISection
              showEmptyValues={false}
              contentDetails={contentDetail}
              includedSections={aiResultResourceSections}
              isEditModeEnabled={false}
              valueColumnAlignRight
              isTextTruncated
              hideFooterButtons
            />
          </SectionContentLayout>
        ))}
    </ProjectDetailViewSkeleton>
  );
};
